import React from 'react';
import { Project, Expense } from '../../types';
import BudgetIndicator from '../dashboard/BudgetIndicator';
import BudgetChart from './BudgetChart';
import AttachmentList from '../expenses/AttachmentList';
import InvoiceSummaryCard from './InvoiceSummaryCard';
import { useProjectInvoiceSummary } from '../../hooks/useInvoices';

interface ProjectDetailProps {
  project: Project;
  expenses: Expense[];
}

const ProjectDetail: React.FC<ProjectDetailProps> = ({ project, expenses }) => {
  const { data: invoiceSummary, isLoading: invoiceSummaryLoading, isError: invoiceSummaryError } = useProjectInvoiceSummary(project.projectId);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDueDateStatus = (dueDate?: string) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate + 'T00:00:00');
    const diffMs = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { color: 'bg-red-100 text-red-800', borderColor: 'border-red-300', iconColor: 'text-red-500', dot: 'bg-red-500', label: `Vencido hace ${Math.abs(diffDays)} día(s)`, urgent: true };
    }
    if (diffDays === 0) {
      return { color: 'bg-red-100 text-red-800', borderColor: 'border-red-300', iconColor: 'text-red-500', dot: 'bg-red-500', label: 'Vence hoy', urgent: true };
    }
    if (diffDays <= 7) {
      return { color: 'bg-red-100 text-red-800', borderColor: 'border-red-300', iconColor: 'text-red-500', dot: 'bg-red-500', label: `Vence en ${diffDays} día(s)`, urgent: true };
    }
    if (diffDays <= 30) {
      return { color: 'bg-yellow-100 text-yellow-800', borderColor: 'border-yellow-300', iconColor: 'text-yellow-500', dot: 'bg-yellow-500', label: `Vence en ${diffDays} días`, urgent: false };
    }
    return { color: 'bg-green-100 text-green-800', borderColor: 'border-green-300', iconColor: 'text-green-500', dot: 'bg-green-500', label: `Vence en ${diffDays} días`, urgent: false };
  };

  const totalExpenses = expenses.length;
  const averageExpense =
    totalExpenses > 0 ? project.spent / totalExpenses : 0;
  const dueDateStatus = getDueDateStatus(project.dueDate);

  return (
    <div className="space-y-6">
      {/* Project Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {project.name}
              </h1>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  project.status === 'active'
                    ? 'bg-blue-100 text-blue-800'
                    : project.status === 'completed'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {project.status === 'active'
                  ? 'Activo'
                  : project.status === 'completed'
                  ? 'Completado'
                  : 'Suspendido'}
              </span>
            </div>
            {project.description && (
              <p className="text-gray-600 mb-4">{project.description}</p>
            )}

            {/* Due Date Banner */}
            {dueDateStatus && (
              <div className={`mb-4 flex items-center gap-3 px-4 py-3 rounded-lg border ${dueDateStatus.color} ${dueDateStatus.borderColor}`}>
                <svg className={`w-5 h-5 flex-shrink-0 ${dueDateStatus.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <span className="font-medium">{dueDateStatus.label}</span>
                  <span className="mx-2">·</span>
                  <span>Fecha límite: {new Date(project.dueDate + 'T00:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>Creado: {formatDate(project.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>{totalExpenses} gastos registrados</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Budget Overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Estado del Presupuesto
        </h2>
        <BudgetIndicator
          budget={project.budget}
          spent={project.spent}
          available={project.available}
          budgetStatus={project.budgetStatus}
          size="lg"
          showDetails={true}
        />
      </div>

      {/* Budget Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Visualización del Presupuesto
        </h2>
        <BudgetChart
          budget={project.budget}
          spent={project.spent}
          available={project.available}
          budgetStatus={project.budgetStatus}
        />
      </div>

      {/* Invoice Summary */}
      <InvoiceSummaryCard projectId={project.projectId} />

      {/* Margen */}
      {invoiceSummary && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Margen del Proyecto
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-lg p-4 ${
              (invoiceSummary.totalInvoiced - project.spent) >= 0 ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Margen (Facturado - Gastado)
              </p>
              <p className={`text-2xl font-bold ${
                (invoiceSummary.totalInvoiced - project.spent) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(invoiceSummary.totalInvoiced - project.spent)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-600 mb-1">
                Total Facturado
              </p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(invoiceSummary.totalInvoiced)}
              </p>
            </div>
          </div>
        </div>
      )}
      {invoiceSummaryLoading && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        </div>
      )}
      {invoiceSummaryError && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-red-600">Error al cargar ingresos</p>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total de Gastos
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {totalExpenses}
              </p>
            </div>
            <div className="bg-blue-100 rounded-lg p-3">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Gasto Promedio
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(averageExpense)}
              </p>
            </div>
            <div className="bg-green-100 rounded-lg p-3">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                % Utilizado
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {((project.spent / project.budget) * 100).toFixed(1)}%
              </p>
            </div>
            <div
              className={`rounded-lg p-3 ${
                project.budgetStatus === 'over'
                  ? 'bg-red-100'
                  : 'bg-yellow-100'
              }`}
            >
              <svg
                className={`w-6 h-6 ${
                  project.budgetStatus === 'over'
                    ? 'text-red-600'
                    : 'text-yellow-600'
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Gastos del Proyecto
          </h2>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Proveedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoría
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subcategoría
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Adjuntos
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense.expenseId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(expense.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {expense.provider}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {expense.categoryName}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {expense.subcategoryName}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {expense.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                    {formatCurrency(expense.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <AttachmentList attachmentIds={expense.attachmentIds || []} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-gray-200">
          {expenses.map((expense) => (
            <div key={expense.expenseId} className="p-4 hover:bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {expense.provider}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(expense.date)}
                  </p>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(expense.amount)}
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {expense.categoryName}
                  </span>
                  <span className="text-xs text-gray-600">
                    {expense.subcategoryName}
                  </span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {expense.description}
                </p>
                {expense.attachmentIds && expense.attachmentIds.length > 0 && (
                  <div className="mt-2">
                    <AttachmentList attachmentIds={expense.attachmentIds} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {expenses.length === 0 && (
          <div className="p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600">
              No hay gastos registrados para este proyecto
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
